(function() {
  'use strict';

  // ============================================
  // CONFIGURATION
  // ============================================
  const TIER_CONFIG = [
    { minQty: 3, sellingPlanId: '6154223859', pricePerUnit: 2300, label: '45' },
    { minQty: 2, sellingPlanId: '6154191091', pricePerUnit: 2700, label: '35' },
    { minQty: 1, sellingPlanId: '6154158323', pricePerUnit: 2900, label: '25' }
  ];

  // Original variant price in cents (before any subscription discount)
  const ORIGINAL_PRICE_CENTS = 3600;

  const ALL_TIER_PLAN_IDS = TIER_CONFIG.map(t => t.sellingPlanId);

  // ============================================
  // HELPERS
  // ============================================

  function getTierForQty(totalQty) {
    for (const tier of TIER_CONFIG) {
      if (totalQty >= tier.minQty) return tier;
    }
    return TIER_CONFIG[TIER_CONFIG.length - 1];
  }

  function isBundleSubscriptionItem(item) {
    if (!item.selling_plan_allocation) return false;
    return ALL_TIER_PLAN_IDS.includes(String(item.selling_plan_allocation.selling_plan.id));
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  // ============================================
  // OPTIMISTIC UI — Instant DOM updates
  // ============================================

  function optimisticUpdate(newTotalQty) {
    const tier = getTierForQty(newTotalQty);

    // --- Update each cart item's inline pricing ---
    const cartItems = document.querySelectorAll('.cart-item');
    cartItems.forEach(item => {
      // Check if this is a subscription item by looking for "Delivery every 30 days"
      const deliveryLabel = item.querySelector('.product-option');
      if (!deliveryLabel || !deliveryLabel.textContent.includes('Delivery every')) return;

      // Get quantity from the input
      const qtyInput = item.querySelector('.quantity__input');
      if (!qtyInput) return;
      const qty = parseInt(qtyInput.value) || 1;

      const originalTotal = ORIGINAL_PRICE_CENTS * qty;
      const sellingTotal = tier.pricePerUnit * qty;
      const savings = originalTotal - sellingTotal;

      // Find the inline price container
      const priceContainer = item.querySelector('.cart-item__inline-price');
      if (!priceContainer) return;

      if (savings > 0) {
        priceContainer.innerHTML =
          '<s class="cart-item__old-price">' + formatMoney(originalTotal) + '</s>' +
          '<strong class="cart-item__sale-price">' + formatMoney(sellingTotal) + '</strong>' +
          '<span class="cart-item__savings-badge">' + formatMoney(savings) + ' off</span>';
      } else {
        priceContainer.innerHTML =
          '<strong class="cart-item__sale-price">' + formatMoney(originalTotal) + '</strong>';
      }
    });

    // --- Update the congrats banner ---
    const banner = document.querySelector('.cart-savings-banner');
    if (banner) {
      banner.querySelector('span').innerHTML =
        '🎉 Congrats! You\'re saving <strong>' + tier.label + '% OFF</strong> on your first subscription.';
    }

    // --- Update subtotal (optimistic estimate) ---
    // We calculate total from all visible cart items
    updateOptimisticSubtotal();
  }

  function updateOptimisticSubtotal() {
    // Sum up all visible prices for a quick subtotal estimate
    const allSalePrices = document.querySelectorAll('.cart-item__sale-price');
    let total = 0;
    allSalePrices.forEach(el => {
      const text = el.textContent.replace('$', '').replace(',', '');
      const val = parseFloat(text);
      if (!isNaN(val)) total += val;
    });

    const subtotalEl = document.querySelector('.totals__total');
    if (subtotalEl && total > 0) {
      // Format without .00 to match existing style
      const formatted = total % 1 === 0
        ? '$' + total.toFixed(0) + '.00 USD'
        : '$' + total.toFixed(2) + ' USD';
      subtotalEl.textContent = formatted.replace('.00', '') + '';
      // Clean up: match the existing format
      subtotalEl.textContent = '$' + total.toFixed(2).replace('.00', '') + ' USD';
    }
  }

  // ============================================
  // CORE — Background plan swap
  // ============================================

  let isProcessing = false;

  async function checkAndFixTiers(cartData) {
    if (isProcessing) return;

    try {
      let cart = cartData;
      if (!cart || !cart.items) {
        const res = await fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } });
        cart = await res.json();
      }

      const bundleItems = cart.items.filter(isBundleSubscriptionItem);
      if (bundleItems.length === 0) return;

      const totalQty = bundleItems.reduce((sum, item) => sum + item.quantity, 0);
      const tier = getTierForQty(totalQty);
      const correctPlanId = tier.sellingPlanId;

      // ---- OPTIMISTIC: Update UI immediately ----
      optimisticUpdate(totalQty);

      const itemsToSwap = bundleItems.filter(item =>
        String(item.selling_plan_allocation.selling_plan.id) !== correctPlanId
      );

      if (itemsToSwap.length === 0) return;

      // Lock processing
      isProcessing = true;

      // Subtle loading indicator (light fade, not jarring)
      const cartItemsEl = document.getElementById('CartDrawer-CartItems');
      if (cartItemsEl) {
        cartItemsEl.style.transition = 'opacity 0.2s ease';
        cartItemsEl.style.opacity = '0.7';
        cartItemsEl.style.pointerEvents = 'none';
      }

      // Collect items to re-add
      const reAddList = itemsToSwap.map(item => ({
        id: item.variant_id,
        quantity: item.quantity,
        selling_plan: parseInt(correctPlanId)
      }));

      // Remove items on wrong plan
      const updates = {};
      itemsToSwap.forEach(item => { updates[item.key] = 0; });

      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      });

      // Re-add with correct plan
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: reAddList })
      });

      // Refresh cart drawer with real server data
      await refreshCartDrawer();

      // Restore opacity
      const cartItemsElAfter = document.getElementById('CartDrawer-CartItems');
      if (cartItemsElAfter) {
        cartItemsElAfter.style.opacity = '';
        cartItemsElAfter.style.pointerEvents = '';
      }

    } catch (err) {
      console.error('[Tier Switcher] Error:', err);
      // Restore on error
      const cartItemsEl = document.getElementById('CartDrawer-CartItems');
      if (cartItemsEl) {
        cartItemsEl.style.opacity = '';
        cartItemsEl.style.pointerEvents = '';
      }
    }

    isProcessing = false;
  }

  // ============================================
  // CART DRAWER REFRESH
  // ============================================

  async function refreshCartDrawer() {
    try {
      const cartUrl = (window.routes && window.routes.cart_url) ? window.routes.cart_url : '/cart';

      const [drawerRes, bubbleRes] = await Promise.all([
        fetch(cartUrl + '?section_id=cart-drawer'),
        fetch(cartUrl + '?section_id=cart-icon-bubble').catch(() => null)
      ]);

      const html = await drawerRes.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      // Replace cart drawer items
      const currentItems = document.querySelector('cart-drawer-items');
      const newItems = doc.querySelector('cart-drawer-items');
      if (currentItems && newItems) currentItems.replaceWith(newItems);

      // Replace footer (subtotal)
      const currentFooter = document.querySelector('.cart-drawer__footer');
      const newFooter = doc.querySelector('.cart-drawer__footer');
      if (currentFooter && newFooter) currentFooter.replaceWith(newFooter);

      // Replace free shipping bar OR savings banner
      const currentShipping = document.querySelector('.free_shipping_cover');
      const currentBanner = document.querySelector('.cart-savings-banner');
      const newShipping = doc.querySelector('.free_shipping_cover');
      const newBanner = doc.querySelector('.cart-savings-banner');

      if (currentShipping) currentShipping.remove();
      if (currentBanner) currentBanner.remove();

      const allHeaders = document.querySelectorAll('.drawer__header');
      const drawerHeader = allHeaders.length > 1 ? allHeaders[allHeaders.length - 1] : allHeaders[0];
      if (newBanner && drawerHeader) {
        drawerHeader.insertAdjacentElement('afterend', newBanner);
      } else if (newShipping && drawerHeader) {
        drawerHeader.insertAdjacentElement('afterend', newShipping);
      }

      // Update cart count in drawer header
      const headerH4 = drawerHeader ? drawerHeader.querySelector('h4') : null;
      const newH4 = doc.querySelector('.drawer__header h4');
      if (headerH4 && newH4) headerH4.innerHTML = newH4.innerHTML;

      // Update cart icon bubble
      if (bubbleRes && bubbleRes.ok) {
        try {
          const bubbleHtml = await bubbleRes.text();
          const bubbleDoc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
          const currentBubble = document.getElementById('cart-icon-bubble');
          const newBubble = bubbleDoc.querySelector('.shopify-section');
          if (currentBubble && newBubble) currentBubble.innerHTML = newBubble.innerHTML;
        } catch (e) {}
      }

      // Update empty state
      const currentDrawer = document.querySelector('cart-drawer');
      const newDrawer = doc.querySelector('cart-drawer');
      if (currentDrawer && newDrawer) {
        currentDrawer.classList.toggle('is-empty', newDrawer.classList.contains('is-empty'));
      }

    } catch (err) {
      console.error('[Tier Switcher] Refresh error:', err);
      window.location.reload();
    }
  }

  // ============================================
  // INIT — Hook into theme pub/sub
  // ============================================

  function init() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source === 'tier-switcher') return;
        checkAndFixTiers(event.cartData || null);
      });
    } else {
      const waitInterval = setInterval(() => {
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          clearInterval(waitInterval);
          subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (event.source === 'tier-switcher') return;
            checkAndFixTiers(event.cartData || null);
          });
        }
      }, 200);
      setTimeout(() => clearInterval(waitInterval), 10000);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
